import React, { useState, useMemo, useRef, useEffect } from 'react';
import Link from 'next/link';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useTheme from '@cvent/blocks/hooks/useTheme';
import useBreakpoints from '@hooks/useBreakpoints';
import { Col } from '@cvent/carina/components/Col';
import { Row } from '@cvent/carina/components/Row';
import { CSSObject } from '@emotion/react';
import { MoodThemeProvider } from '@components/blocks/BlockThemeProvider';
import { isMobile, isTablet } from 'react-device-detect';
import { Keys } from '@tools/KeyConstants';
import Icon from '@components/shared/Icon';
import Typography from '@cvent/blocks/components/Typography';
import isAbsoluteUrl from './isAbsoluteUrl';

export interface MenuItem {
  title: string;
  url: { href: string };
  subItems: Array<{
    title: string;
    url: object | string;
  }>;
  testID?: string;
  // Allow for additional actions when item is clicked
  onClick?: () => void;
}

interface Props {
  /** Trigger to show menu on hover */
  navItem: {
    title: string;
    order: string;
  };
  /** Navigation items. Must include a top-level heading for the menu, and
   * individual dropdown menu items
   */
  menuItems: MenuItem[];
  /** Identifier for page group node */
  pageGroupID: string;
  /**
   * Menu component to display on the left of menu items.
   */
  menuComponent?: React.ReactNode;
}

const useStyles = ({ colCount }: { colCount: number }): Record<string, CSSObject | Record<string, CSSObject>> => {
  const { backgroundColor, font, size } = useTheme();
  const containerOffset = colCount > 2 ? 50 + 5 * (colCount - 2) : 50;

  return {
    trigger: {
      container: {
        position: 'relative',
        display: 'inline-block',
        margin: '0 0 0 10px',
        '&:focus': {
          outline: 'none'
        }
      },
      dropdown: {
        width: '100%',
        height: 52,
        padding: '0 12px',
        display: 'flex',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        color: font.color.base,
        fontSize: font.base.size.m,
        whiteSpace: 'nowrap',
        fontWeight: font.base.weight.regular,
        borderRadius: size.border.radius.fiveX,
        background: 'none',
        border: 'none',
        svg: {
          flexGrow: 1,
          flexShrink: 0
        },
        cursor: 'pointer'
      },
      dropdownActive: {
        backgroundColor: backgroundColor.brand.hover
      }
    },
    spacer: {
      left: '50%',
      bottom: 0,
      transform: `translate(-${containerOffset}%, 100%)`,
      position: 'absolute',
      overflow: 'hidden',
      padding: '0',
      width: '100%',
      height: 'auto',
      zIndex: '400'
    },
    lineClamp: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      wordBreak: 'break-word',
      whiteSpace: 'initial',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
      textAlign: 'center',
      minWidth: '50px'
    },
    chevronIcon: {
      display: 'flex',
      padding: '0 5px'
    }
  };
};

/**
 * MENU COLUMNS ASSEMBLY
 *
 * The purpose of this logic is to assemble an array of columns, which are then rendered in the
 * menu dropdown.
 *
 * Each column can have no more than 10 items. An "item" is defined as one of the following:
 * -> a link to a custom page
 * -> a heading for a grouping of pages
 * -> a separator between page groups
 *
 * A column may NOT end in a heading or a separator. If a new page grouping is supposed to start
 * at element #10 of a column, the start of that grouping is pushed to the next column. In this case,
 * a lone separator should not be rendered at the bottom of the previous column.
 *
 * If 5 columns are completely filled, the maximum length of a column increases by 10 items,
 * and then we re-calculate the columns once more.
 */
export const getAllCols = (menuItems: MenuItem[]): MenuItem[][] => {
  let cols = [[]];
  let currentCol = 0;
  let lengthMultiplier = 0;
  let calculate = true;

  const pushSubItem = subItem => {
    if (cols[currentCol].length >= 10 * (lengthMultiplier + 1)) {
      currentCol++;
      cols.push([subItem]);
    } else {
      cols[currentCol].push({ ...subItem });
    }
  };

  const calculateCols = item => {
    // if there are grouped pages
    if (item?.subItems) {
      // if current column length is less than 8, there's room for a separator
      // but do not add a separator if it is the first column and row
      if (cols[currentCol].length < 8 + 10 * lengthMultiplier && !(currentCol === 0 && cols[currentCol].length === 0)) {
        cols[currentCol].push({ separator: true });
      }
      if (cols[currentCol].length >= 8 + 10 * lengthMultiplier) {
        // if current column length is 8 or more AND latest element is a separator, fill remainder of row
        if (cols[currentCol][cols[currentCol].length - 1]?.separator) {
          cols[currentCol].push({ title: item?.title, id: item?.id });
          if (item?.subItems?.map) {
            item.subItems.forEach(subItem => {
              pushSubItem(subItem);
            });
          }
        } else {
          // else, start a new column
          currentCol++;
          cols.push([{ title: item?.title, id: item?.id }]);
          if (item?.subItems?.map) {
            item.subItems.forEach(subItem => {
              pushSubItem(subItem);
            });
          }
        }
      } else {
        // else, fill remainder of current column with new group items
        cols[currentCol].push({ title: item?.title, id: item?.id });
        if (item?.subItems?.map) {
          item.subItems.forEach(subItem => {
            pushSubItem(subItem);
          });
        }
      }
    } else if (cols[currentCol].length === 10 * (lengthMultiplier + 1)) {
      // else if current column has 10 items, start a new one
      currentCol++;
      cols.push([{ ...item }]);
    } else {
      // else push item into current column
      cols[currentCol].push({ ...item });
    }
  };

  do {
    if (menuItems?.map) {
      menuItems.map(calculateCols);
    }
    if (cols.length <= 5) {
      calculate = false;
    } else {
      cols = [[]];
      currentCol = 0;
      lengthMultiplier++;
    }
  } while (calculate);
  return cols;
};

const useMenuStyles = ({
  colCount,
  hasMenuComponent = false
}: {
  colCount: number;
  hasMenuComponent?: boolean;
}): Record<string, CSSObject> => {
  const { depth, backgroundColor, borderColor, font, size } = useTheme();
  const { heightWindow, contentTop } = useBreakpoints();
  const { xOffset, yOffset, blurRadius, spreadRadius, color } = depth.boxShadow.hard;
  const containerOffset = colCount > 2 ? 50 + 5 * (colCount - 2) : 50;

  return {
    menu: {
      active: {
        visibility: 'visible',
        left: '50%'
      },
      container: {
        background: backgroundColor.base,
        color: font.color.base,
        boxShadow: `${xOffset} ${yOffset} ${blurRadius} ${spreadRadius} ${color}`,
        left: '50%',
        bottom: -12,
        transform: `translate(-${containerOffset}%, 100%)`,
        position: 'absolute',
        borderRadius: size.border.radius.fiveX,
        overflowX: 'hidden',
        overflowY: 'auto',
        padding: hasMenuComponent ? 24 : 0,
        marginTop: 16,
        fontSize: font.base.size.s,
        visibility: 'hidden',
        width: 'max-content',
        height: 'auto',
        zIndex: '400',
        maxHeight: heightWindow - contentTop * 0.75
      },
      link: {
        display: 'inline-block',
        width: '100%',
        whiteSpace: 'nowrap',
        '& a': {
          width: '100%',
          height: '100%',
          textDecoration: 'none',
          color: font.color.base,
          display: 'block',
          boxSizing: 'border-box',
          padding: 8,
          borderRadius: size.border.radius.threeX,
          whiteSpace: 'normal',
          fontSize: font.base.size.m,
          fontWeight: font.base.weight.regular,
          '&:hover': {
            backgroundColor: backgroundColor.neutral.focus
          },
          '&:focus': {
            outline: 'none',
            backgroundColor: backgroundColor.neutral.focus
          }
        }
      }
    },
    heading: {
      textTransform: 'uppercase',
      color: font.color.soft,
      display: 'inline-block',
      margin: 0,
      padding: hasMenuComponent ? '0px 16px 2px 8px' : '8px 16px 2px 8px',
      '&:hover': {
        backgroundColor: 'transparent !important'
      }
    },
    separator: {
      borderBottom: `solid ${size.border.base}px ${borderColor.soft}`,
      marginLeft: '8px',
      marginRight: '8px',
      display: 'block',
      height: 6,
      marginBottom: 6
    }
  };
};

interface PageGroupMenuProps {
  menuCols: (MenuItem | { separator: true })[][];
  pageGroupID: string;
  isOpen: boolean;
  setIsOpen: (boolean) => void;
  menuComponent: React.ReactNode;
}

function PageGroupMenu({ menuCols, pageGroupID, isOpen, setIsOpen, menuComponent }: PageGroupMenuProps): JSX.Element {
  const hasMenuComponent = !!menuComponent;
  const styles = useMenuStyles({
    colCount: menuCols?.length,
    hasMenuComponent
  });

  let headingIndex = -1;

  return (
    <div css={[styles.menu.container, isOpen ? styles.menu.active : null]}>
      <Row>
        {hasMenuComponent && (
          <Col padding={{ start: 0, end: 0 }} offsetEnd={16} width="content">
            {menuComponent}
          </Col>
        )}
        {menuCols?.map((col, colIndex) => (
          <Col key={`${pageGroupID}-column-${colIndex}`} padding={{ top: 8, start: 8, end: 8, bottom: 8 }} width={214}>
            {col?.map((row, index) => {
              if ('separator' in row) {
                return <div key={`${pageGroupID}-item-col${colIndex}-row${index}`} css={styles.separator} />;
              }
              if (!row.url) {
                headingIndex++;
                return (
                  <Typography
                    as="h3"
                    styledAs="h5"
                    key={`${pageGroupID}-item-col${colIndex}-row${index}`}
                    css={styles.heading}
                    {...injectTestId(`${pageGroupID}-item-heading-${headingIndex}`)}
                  >
                    {row.title}
                  </Typography>
                );
              }
              return (
                <ul css={{ padding: 0 }}>
                  <li
                    key={`${pageGroupID}-item-col${colIndex}-row${index}`}
                    css={styles.menu.link}
                    {...injectTestId(`${pageGroupID}-item-${index}`)}
                  >
                    <Link
                      href={row?.url?.href || ''}
                      passHref
                      target={isAbsoluteUrl(row?.url?.href) ? '_blank' : '_self'}
                      onClick={() => {
                        setIsOpen(!isOpen);
                        if (row?.onClick) {
                          row.onClick();
                        }
                      }}
                      onKeyDown={event => {
                        if (event.key === Keys.SPACE) {
                          event.preventDefault();
                          window.location.href = `/hub${row?.url?.href}`;
                        }
                      }}
                      {...injectTestId(row?.testID)}
                    >
                      {row?.title}
                    </Link>
                  </li>
                </ul>
              );
            })}
          </Col>
        ))}
      </Row>
    </div>
  );
}

function PageGroup({ navItem, menuItems, pageGroupID, menuComponent }: Props): JSX.Element {
  const { font } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const listItemRef = useRef<HTMLLIElement>(null);

  const menuCols = useMemo(() => getAllCols(menuItems), [menuItems]);

  const { trigger, spacer, lineClamp, chevronIcon } = useStyles({
    colCount: menuCols?.length
  });

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent): void => {
      if (!listItemRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  return (
    <li
      css={trigger.container}
      id={pageGroupID}
      {...injectTestId(`${pageGroupID}-trigger`)}
      onMouseEnter={isMobile || isTablet ? null : () => setIsOpen(true)}
      onMouseLeave={
        isMobile || isTablet
          ? null
          : () => {
              setIsOpen(false);
              // Scroll to top of container on close, so that we always start at the top upon opening
              containerRef.current.scrollTop = 0;
            }
      }
      onBlur={(e: React.FocusEvent<HTMLLIElement>): void => {
        if (!isMobile && !isTablet && !e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsOpen(false);
        }
      }}
      ref={listItemRef}
    >
      <button
        css={isOpen ? [trigger.dropdown, trigger.dropdownActive] : trigger.dropdown}
        tabIndex={0}
        key={`global-nav-item-${navItem.order}`}
        onClick={e => {
          containerRef.current.scroll(0, 0);
          e.preventDefault();
          setIsOpen(true);
        }}
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onKeyDown={event => {
          if (event.key === Keys.SPACE) {
            event.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        <div css={lineClamp}>{navItem?.title}</div>
        <div css={chevronIcon}>
          {isOpen ? (
            <Icon name="chevron-up" size="xs" color={font.color.base} aria-hidden />
          ) : (
            <Icon name="chevron-down" size="xs" color={font.color.base} aria-hidden />
          )}
        </div>
      </button>
      <div css={{ ...spacer, width: '100%', height: 12 }} />
      <div ref={containerRef}>
        <MoodThemeProvider>
          <PageGroupMenu
            menuCols={menuCols}
            pageGroupID={pageGroupID}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            menuComponent={menuComponent}
          />
        </MoodThemeProvider>
      </div>
    </li>
  );
}

export default PageGroup;
